import { useIsMobile } from '@newfront-insurance/admin-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import type { ReactNode } from 'react';

import { useChatOverlayContext } from '../contexts/chat-overlay-context';

interface ChatContainerProps {
  children: ReactNode;
  className?: string;
  isVisible?: boolean;
}

/**
 * Container wrapper component that handles the main chat container styling and layout.
 * Uses context to determine fullscreen and sidebar state for conditional styling.
 * When isVisible is false, the container is hidden but remains mounted to preserve state.
 */
export function ChatContainer({ children, className, isVisible = true }: ChatContainerProps) {
  const { isSidebarOpen, isFullscreen, isEmbedded, chatUuid } = useChatOverlayContext();
  const { isMobile } = useIsMobile();
  const shouldFillViewport = isFullscreen || isMobile || isEmbedded;

  return (
    <div
      data-testid="chat-container"
      data-chat-id={chatUuid}
      className={cn(
        'fixed right-0 bottom-0 z-[1000]',
        'bg-white',
        'flex flex-row',
        'border',
        {
          'h-[100dvh] w-[100dvw] rounded-none border-transparent': shouldFillViewport,
          'border-steel-200 right-9 h-[max(50dvh,650px)] w-[800px] min-w-[600px] rounded-t-sm': !shouldFillViewport,
          'rounded-tl-none': isSidebarOpen && !shouldFillViewport,
          'shadow-floating-bar': !isEmbedded,
          'transition-[width,height,right,border-color,border-radius] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none':
            !isEmbedded,
        },
        className,
      )}
      style={{ display: isVisible ? 'flex' : 'none' }}
    >
      {children}
    </div>
  );
}
